import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import DishesSection from '../components/DishesSection';
import './Story.css';

// Import local assets
import heroImg from '../assets/img2.png';
import philosophyImg from '../assets/masala.jpg';
import visionImg from '../assets/img3.png';
import missionImg from '../assets/masala1.jpg';
import value1Img from '../assets/masala2.jpg';
import value2Img from '../assets/masala3.jpg';
import value3Img from '../assets/masala4.jpg';
import value4Img from '../assets/masala5.jpg';
import farmerImg from '../assets/spices4.jpg';

const Story = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });

  const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const timelineEvents = [
    {
      year: '2005',
      title: 'Brand Foundation',
      desc: 'Shri Vadilal Bhai Shah registered the brand Ragini and launched first 3 products: Milk Masala, Garam Masala & Tea Masala.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      year: '2012',
      title: 'Organizational Growth',
      desc: 'Our second generation joined the organization, expanding our distribution network across Maharashtra.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20v-8m0 0l-4 4m4-4l4 4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 4v8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      year: '2019',
      title: 'Pan-India Awareness',
      desc: 'Ragini held its first major dealer meet to promote pure stone-ground spices and create nationwide brand awareness.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 5h2M11 9h2M11 13h2M11 17h2" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      )
    },
    {
      year: '2024',
      title: 'Culinary Legacy',
      desc: 'Reaching 1 Million+ kitchens with our commitment to zero-adulteration purity and traditional aroma.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 22l1-1h18l1 1M12 2v20" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
  ];

  return (
    <div className="story-page" ref={containerRef}>
      {/* Hero Section — ORIGINAL STABLE */}
      <section className="story-hero">
        <motion.div className="story-hero-bg" style={{ scale }}>
          <img src={heroImg} alt="Heritage Spices" />
          <div className="hero-overlay" />
        </motion.div>

        <div className="container">
          <motion.div
            className="story-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <span className="section-tag" style={{ color: 'var(--saffron)' }}>Since 2005</span>
            <h1 className="hero-title">Our <span className="gradient-text">Heritage</span></h1>
            <p className="hero-subtext">A journey of thousand miles, beginning with a single pinch of pure masala.</p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="story-philosophy">
        <div className="container">
          <div className="philosophy-grid">
            <div className="philosophy-text">
              <h2 className="section-title">The Philosophy of <span className="gradient-text">Purity</span></h2>
              <p>At Ragini Masale, we believe that spices are not just ingredients; they are the soul of the kitchen. Our philosophy is rooted in the ancient Indian tradition of hand-grinding.</p>
            </div>
            <div className="philosophy-image">
              <img src={philosophyImg} alt="Authentic Grinding" />
            </div>
          </div>
        </div>
      </section>

      {/* LEGACY JOURNEY — FINAL PERFECT VERSION (VERTICAL STACK) */}
      <section className="journey-curved-section rounded-top-fix">
        <div className="container">
          <div className="journey-header text-center">
            <h2 className="journey-main-title">Our Journey</h2>
            <p className="journey-sub">Ragini’s great taste of purity is backed up with research, innovation and technology.</p>
          </div>

          <div className="journey-wrapper">
            <div className="journey-path-container">
              <svg viewBox="0 0 1000 1000" fill="none" preserveAspectRatio="none">
                <path
                  d="M500,0 C900,250 100,450 500,600 C900,750 100,950 500,1000"
                  stroke="#FF4D4D"
                  strokeWidth="2"
                  strokeDasharray="12 12"
                  opacity="0.2"
                />
              </svg>
            </div>

            <div className="journey-items-stack">
              {timelineEvents.map((event, i) => (
                <motion.div
                  key={i}
                  className={`journey-card-node ${i % 2 === 0 ? 'pos-left' : 'pos-right'}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="card-node-inner">
                    <div className="card-node-icon">
                      <div className="node-circle">{event.icon}</div>
                    </div>
                    <div className="card-node-text">
                      <span className="node-year">{event.year}</span>
                      <h3 className="node-title">{event.title}</h3>
                      <p className="node-desc">{event.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION — MOODY EDITORIAL SPLIT */}
      <section className="story-vision-mission">
        <div className="vision-block">
          <motion.div
            className="vision-bg"
            initial={{ scale: 1.2 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 2 }}
          >
            <img src={visionImg} alt="Vision" />
          </motion.div>
          <div className="vision-content">
            <motion.span className="section-tag" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} style={{ color: 'var(--saffron)' }}>Our Vision</motion.span>
            <motion.h2 className="editorial-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}>
              The <span className="serif-italic">Horizon</span>
            </motion.h2>
            <motion.p className="editorial-p" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              To be the global benchmark for culinary purity, preserving the ancient wisdom of stone-grinding for generations to come.
            </motion.p>
          </div>
        </div>

        <div className="mission-block">
          <motion.div
            className="mission-bg"
            initial={{ scale: 1.2 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 2 }}
          >
            <img src={missionImg} alt="Mission" />
          </motion.div>
          <div className="mission-content">
            <motion.span className="section-tag" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} style={{ color: 'var(--saffron)' }}>Our Mission</motion.span>
            <motion.h2 className="editorial-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}>
              The <span className="serif-italic">Vow</span>
            </motion.h2>
            <motion.p className="editorial-p" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              To empower small-hold farmers and deliver authentic, chemical-free spices that bring soul back to the modern kitchen.
            </motion.p>
          </div>
        </div>
      </section>

      {/* CORE VALUES — INNOVATIVE EXHIBITION */}
      <section className="story-core-values">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-tag">Our Soul</span>
            <h2 className="section-title">Our <span className="serif-italic">Core Values</span></h2>
          </div>
          
          <div className="values-frames-grid">
            <motion.div 
              className="value-frame" 
              initial={{ opacity: 0, y: 50 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="value-img-wrap">
                <img src={value1Img} alt="Purity" />
                <div className="value-overlay" />
              </div>
              <div className="value-content">
                <h3 className="value-title">Purity</h3>
                <p className="value-desc">Zero-compromise on traditional stone-grinding methods.</p>
              </div>
            </motion.div>

            <motion.div 
              className="value-frame" 
              initial={{ opacity: 0, y: 50 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="value-img-wrap">
                <img src={value2Img} alt="Heritage" />
                <div className="value-overlay" />
              </div>
              <div className="value-content">
                <h3 className="value-title">Heritage</h3>
                <p className="value-desc">Guarding 50-year-old family recipes and secrets.</p>
              </div>
            </motion.div>

            <motion.div 
              className="value-frame" 
              initial={{ opacity: 0, y: 50 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="value-img-wrap">
                <img src={value3Img} alt="Honesty" />
                <div className="value-overlay" />
              </div>
              <div className="value-content">
                <h3 className="value-title">Honesty</h3>
                <p className="value-desc">Direct farm-to-kitchen sourcing with zero middlemen.</p>
              </div>
            </motion.div>

            <motion.div 
              className="value-frame" 
              initial={{ opacity: 0, y: 50 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="value-img-wrap">
                <img src={value4Img} alt="Quality" />
                <div className="value-overlay" />
              </div>
              <div className="value-content">
                <h3 className="value-title">Quality</h3>
                <p className="value-desc">Small-batch production for maximum aromatic potency.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <DishesSection 
        title="Culinary Inspiration" 
        subtitle="See how our heritage spices transform into masterpieces in the kitchen" 
      />

      {/* Farmer Section */}
      <section className="story-farmers">
        <div className="container">
          <div className="farmers-grid">
            <motion.div className="farmers-image" style={{ y: y2 }}>
              <img src={farmerImg} alt="Our Farmers" />
            </motion.div>

            <motion.div
              className="farmers-text"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="section-tag">Rooted in Earth</span>
              <h2 className="section-title">The Hands Behind <span className="gradient-text">The Flavour</span></h2>
              <p>We work directly with over 500 small-scale farmers, ensuring they get a fair price for their hard work.</p>
              <a href="/contact" className="btn-nav-order" style={{ display: 'inline-block', marginTop: '2rem' }}>Connect with Us</a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Story;
